/* global axios */
/* eslint-disable max-classes-per-file -- resource clients share one module */
import ApiClient from './ApiClient';

const queryString = params => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item === undefined || item === null || item === '') return;
        search.append(key, item);
      });
      return;
    }
    search.append(key, value);
  });
  return search.toString();
};

class WebOrderResourceAPI extends ApiClient {
  list(params = {}) {
    const query = queryString(params);
    return axios.get(query ? `${this.url}?${query}` : this.url);
  }

  bulkUpdate(ids, attributes) {
    return axios.patch(`${this.url}/bulk_update`, {
      ids,
      ...attributes,
    });
  }

  assign(id, userId) {
    return axios.post(`${this.url}/${id}/assign`, { user_id: userId });
  }

  unassign(id) {
    return axios.delete(`${this.url}/${id}/unassign`);
  }

  addNote(id, note) {
    return axios.post(`${this.url}/${id}/add_note`, { note });
  }

  recall(id, recalled) {
    return axios.post(`${this.url}/${id}/recall`, { recalled });
  }

  recordCall(id) {
    return axios.post(`${this.url}/${id}/record_call`);
  }

  receive(id, data = {}) {
    return axios.post(`${this.url}/${id}/receive`, data);
  }

  defaultImpact() {
    return axios.get(`${this.url}/default_impact`);
  }

  agentPerformance(params = {}) {
    const query = queryString(params);
    return axios.get(
      query
        ? `${this.url}/agent_performance?${query}`
        : `${this.url}/agent_performance`
    );
  }

  overviewAnalytics(params = {}) {
    const query = queryString(params);
    return axios.get(
      query
        ? `${this.url}/overview_analytics?${query}`
        : `${this.url}/overview_analytics`
    );
  }

  downloadReportPreview(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(item => {
          if (item === undefined || item === null || item === '') return;
          search.append(`${key}[]`, item);
        });
        return;
      }
      search.append(key, value);
    });
    const query = search.toString();
    return axios.get(
      query
        ? `${this.url}/download_report_preview?${query}`
        : `${this.url}/download_report_preview`
    );
  }

  assignmentCounts() {
    return axios.get(`${this.url}/assignment_counts`);
  }

  assignableAgents() {
    return axios.get(`${this.url}/assignable_agents`);
  }

  filter(params = {}) {
    return axios.post(`${this.url}/filter`, params);
  }

  sync(id) {
    return axios.post(`${this.url}/${id}/sync`);
  }

  markInvoicePrinted(ids) {
    return axios.patch(`${this.url}/mark_invoice_printed`, { ids });
  }

  pickingChecklist(params = {}) {
    const query = queryString(params);
    return axios.get(
      query
        ? `${this.url}/picking_checklist?${query}`
        : `${this.url}/picking_checklist`
    );
  }

  duplicatePhoneStats(params = {}) {
    const query = queryString(params);
    return axios.get(
      query
        ? `${this.url}/duplicate_phone_stats?${query}`
        : `${this.url}/duplicate_phone_stats`
    );
  }
}

class WebCustomersAPI extends WebOrderResourceAPI {
  tags(query = '') {
    const qs = queryString({ q: query });
    return axios.get(qs ? `${this.url}/tags?${qs}` : `${this.url}/tags`);
  }
}

export const webOrdersAPI = new WebOrderResourceAPI('web_orders', {
  accountScoped: true,
});
export const webProductsAPI = new WebOrderResourceAPI('web_products', {
  accountScoped: true,
});
export const webCustomersAPI = new WebCustomersAPI('web_customers', {
  accountScoped: true,
});
export const webOrderPickupAddressesAPI = new WebOrderResourceAPI(
  'web_order_pickup_addresses',
  { accountScoped: true }
);
export const webOrderCustomersAPI = new WebOrderResourceAPI(
  'web_order_customers',
  { accountScoped: true }
);
export const woocommerceStoresAPI = new WebOrderResourceAPI(
  'woocommerce_stores',
  { accountScoped: true }
);
export const deliveryPartnerAccountsAPI = new WebOrderResourceAPI(
  'delivery_partner_accounts',
  { accountScoped: true }
);
export const deliveryShipmentsAPI = new WebOrderResourceAPI(
  'delivery_shipments',
  { accountScoped: true }
);

class InventoryStockAPI extends WebOrderResourceAPI {
  adjust(data) {
    return axios.post(`${this.url}/adjust`, data);
  }

  transfer(data) {
    return axios.post(`${this.url}/transfer`, data);
  }
}

export const inventoryStockAPI = new InventoryStockAPI('inventory/stock', {
  accountScoped: true,
});
export const inventoryLogsAPI = new WebOrderResourceAPI('inventory/logs', {
  accountScoped: true,
});
export const inventoryVendorsAPI = new WebOrderResourceAPI(
  'inventory/vendors',
  {
    accountScoped: true,
  }
);
export const inventoryWarehousesAPI = new WebOrderResourceAPI(
  'inventory/warehouses',
  { accountScoped: true }
);

class InventoryPurchasesAPI extends WebOrderResourceAPI {
  place(id) {
    return axios.post(`${this.url}/${id}/place`);
  }

  receiving(id) {
    return axios.post(`${this.url}/${id}/receiving`);
  }

  receive(id, data = {}) {
    return axios.post(`${this.url}/${id}/receive`, data);
  }
}

export const inventoryPurchasesAPI = new InventoryPurchasesAPI(
  'inventory/purchases',
  { accountScoped: true }
);

class InventoryBillsAPI extends WebOrderResourceAPI {
  issue(id, data = {}) {
    return axios.post(`${this.url}/${id}/issue`, data);
  }

  pay(id, data = {}) {
    return axios.post(`${this.url}/${id}/pay`, data);
  }
}

export const inventoryBillsAPI = new InventoryBillsAPI('inventory/bills', {
  accountScoped: true,
});

class WebOrderSettingAPI extends ApiClient {
  constructor() {
    super('web_order_setting', { accountScoped: true });
  }

  update(data) {
    return axios.patch(this.url, data);
  }
}

export const webOrderSettingsAPI = new WebOrderSettingAPI();

export { queryString };
